// New Customer Intake Review (approved HTML:
// 20-New-Customer-Intake-Consistency-Review-v5.html).
//
// Restricted account-phone connection path for a current unrecognized
// inbound call. There is no general customer search. Ambiguous
// identities are held for authorized resolution. Writes gate on
// LIVE_WRITE_GATE_HUBSPOT_CONTACT_CREATE.
//
// W31 (2026-10-20): on a real session the dock never shows the fixture
// call ("03:42", the sample unknown-caller phone) and the callout never
// names the fixture rep. Connect / Save stay visible and inert behind
// the live-write gate.
//
// W37b (2026-10-20): GET /v1/intake/pending answers 200 since W37a
// (sinks/intake-sink.mjs, READ; MEASURED with the url-bootstrap session:
// `{items: [50 contacts], total: 847643, window: {lookbackDays: 7}}`).
// The rows are HubSpot contacts created inside the window with no owner.
// The whole base was re-imported this week, so that definition matches
// the entire base today; the card says so ("Showing newest unowned
// contacts; intake rules apply after the re-import") instead of calling
// 847,643 contacts "new". The body carries no live call, so the dock
// stays on its honest "No incoming call" facts.
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { Icon } from '../_shared/IconSprite.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { RepriorityPill } from '../_shared/PriorityFocus.js';
import {
  CALL_INFO as CALL_INFO_FIXTURE,
  EMPTY_INTAKE,
  UNKNOWN_CALLER_PHONE as UNKNOWN_CALLER_PHONE_FIXTURE,
  type IntakeDraft,
} from './mockData.js';
import { JAMIE } from '../../data/mockData.js';
import { getReviewReturnTarget } from '../_shared/ReviewState.js';
import { useScopedData } from '../_w2a_lower/scopedData.js';
import { isRecord, monthYear, readOptionalString, readString, useScreenIdentity } from '../_w2a_lower/identity.js';
import { WriteButton } from '../_w2a_lower/liveWrite.js';
import './styles.css';

export const INTAKE_HONEST_NOTE =
  'Showing newest unowned contacts; intake rules apply after the re-import';

/** One pending-intake contact. Row shape READ from sinks/intake-sink.mjs intakeRow(). */
export interface IntakePendingRow {
  id: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  createdAt: string | null;
  lifecycleStage: string | null;
  latestSource: string | null;
}

export interface IntakePendingView {
  items: IntakePendingRow[];
  /** HubSpot's own count for the filter; can exceed the rows returned. */
  total: number | null;
  lookbackDays: number | null;
}

export function mapIntakePending(body: unknown): IntakePendingView {
  const list = isRecord(body) && Array.isArray(body.items) ? body.items : [];
  const items: IntakePendingRow[] = [];
  for (const raw of list) {
    if (!isRecord(raw)) continue;
    const id = readString(raw.id) || readString(raw.contactId);
    if (!id) continue;
    items.push({
      id,
      fullName: readOptionalString(raw.fullName),
      email: readOptionalString(raw.email),
      phone: readOptionalString(raw.phone),
      city: readOptionalString(raw.city),
      state: readOptionalString(raw.state),
      createdAt: readOptionalString(raw.createdAt),
      lifecycleStage: readOptionalString(raw.lifecycleStage),
      latestSource: readOptionalString(raw.latestSource),
    });
  }
  const total = isRecord(body) && typeof body.total === 'number' && Number.isFinite(body.total) ? body.total : null;
  const win = isRecord(body) && isRecord(body.window) ? body.window : null;
  const lookbackDays =
    win && typeof win.lookbackDays === 'number' && Number.isFinite(win.lookbackDays) ? win.lookbackDays : null;
  return { items, total, lookbackDays };
}

// Approved inbound-call account-lookup payload. The intake flow refuses
// to open a general search; the server returns the current call's
// evidence plus a scoped lookup window keyed to the incoming phone. An
// ambiguous match stays explicit on the response.
export interface IntakeCallInfo {
  providerLabel: string;
  providerEvidence: string;
  state: string;
  duration: string;
  callerName: string;
  callerNumber: string;
  initials: string;
}

interface NewCustomerIntakeView {
  callInfo: IntakeCallInfo;
  unknownCallerPhone: string;
}

const NEW_CUSTOMER_INTAKE_FIXTURE: NewCustomerIntakeView = {
  callInfo: CALL_INFO_FIXTURE,
  unknownCallerPhone: UNKNOWN_CALLER_PHONE_FIXTURE,
};

const NO_CALL_INFO: IntakeCallInfo = {
  providerLabel: 'Kixie · Business Id 72616',
  providerEvidence: 'Kixie call state not connected yet',
  state: 'No incoming call',
  duration: '',
  callerName: 'No caller connected',
  callerNumber: '',
  initials: '',
};

/**
 * Dock facts from a 200 body. The W37a body carries no `callInfo` (there
 * is no live-call source), so a real session always lands on the honest
 * "No incoming call" dock; the `callInfo{}` branch stays for a future
 * Kixie inbound read.
 */
export function mapRealIntake(body: unknown): NewCustomerIntakeView {
  const raw = isRecord(body) && isRecord(body.callInfo) ? body.callInfo : null;
  if (!raw) return { callInfo: NO_CALL_INFO, unknownCallerPhone: '' };
  return {
    callInfo: {
      providerLabel: readString(raw.providerLabel) || NO_CALL_INFO.providerLabel,
      providerEvidence: readString(raw.providerEvidence) || 'Kixie provider state',
      state: readString(raw.state) || 'Incoming',
      duration: readString(raw.duration),
      callerName: readString(raw.callerName) || 'Incoming caller',
      callerNumber: readString(raw.callerNumber),
      initials: readString(raw.initials),
    },
    unknownCallerPhone: isRecord(body) ? readString(body.unknownCallerPhone) : '',
  };
}

export interface NewCustomerIntakeScreenProps {
  stateOverride?: BoundaryState;
}

type IntakeMode = 'connect' | 'new' | 'held' | 'saved';

export function NewCustomerIntakeScreen({
  stateOverride,
}: NewCustomerIntakeScreenProps = {}): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routeCustomerId = params.customerId ?? null;
  const identity = useScreenIdentity(routeCustomerId);
  const scoped = useScopedData<unknown>({
    path: '/v1/intake/pending',
    fixture: NEW_CUSTOMER_INTAKE_FIXTURE,
    loadingLabel: 'Loading inbound call…',
    errorTitle: 'Intake could not load',
    errorCode: 'NEW_CUSTOMER_INTAKE_UNAVAILABLE',
    isEmpty: () => false,
    ...(stateOverride !== undefined ? { stateOverride } : {}),
  });
  const useFixture = scoped.previewAllowed;
  const view = useFixture ? NEW_CUSTOMER_INTAKE_FIXTURE : mapRealIntake(scoped.data);
  const pending = useMemo<IntakePendingView>(
    () => (useFixture ? { items: [], total: null, lookbackDays: null } : mapIntakePending(scoped.data)),
    [useFixture, scoped.data],
  );
  const CALL_INFO = view.callInfo;
  const UNKNOWN_CALLER_PHONE = view.unknownCallerPhone;
  const paused = !useFixture;
  const [mode, setMode] = useState<IntakeMode>('connect');
  const [accountPhone, setAccountPhone] = useState('');
  const [draft, setDraft] = useState<IntakeDraft>(EMPTY_INTAKE);
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const state =
    stateOverride ??
    (scoped.state.kind === 'default' ? resolveState(kind, undefined) : scoped.state);
  const returnTarget = getReviewReturnTarget(location.state, routeCustomerId);
  const canSave = Boolean(draft.firstName.trim() && (draft.phone.trim() || draft.email.trim()));
  const finishDisabled = mode !== 'saved';

  function setDraftField<K extends keyof IntakeDraft>(key: K, value: string): void {
    setDraft(prev => ({ ...prev, [key]: value }));
  }

  return (
    <WorkspaceChrome screen="intake" rail="my-work">
      {/* W40: the approved 20 has no breadcrumb row (MEASURED, 0 entries);
          the row stays only as the carrier of the preview label. */}
      {scoped.previewAllowed ? (
        <div className="breadcrumbs">
          <div>
            My work <span className="slash">/</span> <strong>New customer intake</strong>
          </div>
          <span className="preview">Design preview · No live write</span>
        </div>
      ) : null}
      {!useFixture && identity.profile ? (
        <IdentityRow
          preferenceNote="The customer’s request guides this conversation."
          customer={identity.customer}
          badgeLabel="Connected account"
          {...(identity.sinceLabel !== undefined ? { sinceLabel: identity.sinceLabel } : {})}
        />
      ) : null}
      <DataBoundary state={state} testId="new-customer-intake-boundary">
        <section className="i20-banner">
          <div className="i20-banner-content">
            <div className="i20-channel">
              <Icon id="help" />
              <strong>Incoming Call</strong>
            </div>
            <div className="i20-purpose">
              <h1>Connect the caller to their account</h1>
              <p>Use the account phone number, or add a genuinely new customer.</p>
            </div>
            {/* W40: approved pill, informational, on every session. */}
            <RepriorityPill className="shared-repriority" />
          </div>
        </section>
        <section
          className="cc-call-dock"
          aria-label="Active call · Available across the intake"
        >
          <div className="cc-dock-row">
            <div className="cc-dock-caller">
              <div className="cc-call-status">
                <div className="cc-call-avatar">{CALL_INFO.initials}</div>
                <div>
                  <span className="cc-channel-label">INCOMING CALL</span>
                  <h3>{CALL_INFO.callerName}</h3>
                  <p>{CALL_INFO.callerNumber}</p>
                </div>
              </div>
              <div className="cc-dock-channel">
                <span className="badge">{CALL_INFO.providerLabel}</span>
              </div>
            </div>
            <div className="cc-dock-controls">
              <div className="cc-call-clock">
                <span className="cc-state-pill">{CALL_INFO.state}</span>
                {CALL_INFO.duration ? <strong>{CALL_INFO.duration}</strong> : null}
                <small>{CALL_INFO.providerEvidence}</small>
              </div>
              <div className="cc-audio-actions">
                <WriteButton paused={paused} className="cc-end-call" actionLabel="End call">
                  <Icon id="help" />
                  End call
                </WriteButton>
                <WriteButton paused={paused} className="cc-transfer-button" actionLabel="Transfer call">
                  <Icon id="people" />
                  Transfer call
                </WriteButton>
                <WriteButton paused={paused} className="cc-secondary" actionLabel="Mute">
                  Mute
                </WriteButton>
              </div>
            </div>
          </div>
          <div className="cc-dock-return">
            <small>Notes stay with this call</small>
          </div>
        </section>
        <div id="cc-conversation-workspace">
          <div className="cc-grid">
            <div className="cc-stack">
              <section className="card cc-card cc-conversation-card">
                <div className="cc-card-head">
                  <div>
                    <Icon id="help" />
                    <h2>Incoming call</h2>
                  </div>
                </div>
                <div className="cc-conversation-body">
                  <section id="i20-intake" className="i20-intake" aria-label="Connect the incoming caller">
                    {mode === 'connect' ? (
                      <div id="i20-link">
                        <div className="i20-heading">
                          <Icon id="people" />
                          <div>
                            <h3>Already a customer?</h3>
                            <p>Ask for a phone number already on their account.</p>
                          </div>
                        </div>
                        <label className="cc-field" htmlFor="i20-account-phone">
                          Account phone number
                        </label>
                        <div className="i20-link-row">
                          <input
                            id="i20-account-phone"
                            type="tel"
                            autoComplete="off"
                            placeholder="Phone number on the account"
                            value={accountPhone}
                            onChange={event => setAccountPhone(event.target.value)}
                          />
                          <WriteButton
                            paused={paused}
                            id="i20-connect"
                            className="cc-blue"
                            disabled={!accountPhone.trim()}
                            onClick={() =>
                              UNKNOWN_CALLER_PHONE && accountPhone === UNKNOWN_CALLER_PHONE
                                ? setMode('held')
                                : setMode('saved')
                            }
                            actionLabel="Connect account"
                          >
                            Connect account
                            <Icon id="arrow" />
                          </WriteButton>
                        </div>
                        <div className="i20-new-line">
                          <span>Caller does not have an account?</span>
                          <button
                            type="button"
                            id="i20-new"
                            className="cc-secondary"
                            onClick={() => setMode('new')}
                          >
                            New customer
                          </button>
                        </div>
                      </div>
                    ) : null}
                    {mode === 'new' ? (
                      <div id="i20-new-form">
                        <div className="i20-new-heading">
                          <div>
                            <h3>New customer</h3>
                            <p>Save their information and keep helping them on this call.</p>
                          </div>
                          <button
                            id="i20-back"
                            type="button"
                            className="cc-link"
                            onClick={() => setMode('connect')}
                          >
                            Back to account connection
                          </button>
                        </div>
                        <div className="i20-fields">
                          <label>
                            First name
                            <input
                              id="i20-firstName"
                              autoComplete="given-name"
                              value={draft.firstName}
                              onChange={event => setDraftField('firstName', event.target.value)}
                            />
                          </label>
                          <label>
                            Last name
                            <input
                              id="i20-lastName"
                              autoComplete="family-name"
                              value={draft.lastName}
                              onChange={event => setDraftField('lastName', event.target.value)}
                            />
                          </label>
                          <label>
                            Phone
                            <input
                              id="i20-phone"
                              type="tel"
                              autoComplete="tel"
                              value={draft.phone}
                              onChange={event => setDraftField('phone', event.target.value)}
                            />
                          </label>
                          <label>
                            Email
                            <input
                              id="i20-email"
                              type="email"
                              autoComplete="email"
                              value={draft.email}
                              onChange={event => setDraftField('email', event.target.value)}
                            />
                          </label>
                        </div>
                        <p className="i20-contact-hint">
                          Provide a phone number or email address.
                        </p>
                        <details className="i20-extra">
                          <summary>
                            Shipping address &amp; additional details <span>Optional</span>
                          </summary>
                          <div className="i20-fields">
                            <label className="i20-wide">
                              Street address
                              <input
                                id="i20-address1"
                                autoComplete="address-line1"
                                value={draft.address1}
                                onChange={event => setDraftField('address1', event.target.value)}
                              />
                            </label>
                            <label className="i20-wide">
                              Apartment, suite or delivery details
                              <input
                                id="i20-address2"
                                autoComplete="address-line2"
                                value={draft.address2}
                                onChange={event => setDraftField('address2', event.target.value)}
                              />
                            </label>
                            <label>
                              City
                              <input
                                id="i20-city"
                                autoComplete="address-level2"
                                value={draft.city}
                                onChange={event => setDraftField('city', event.target.value)}
                              />
                            </label>
                            <label>
                              State
                              <input
                                id="i20-state"
                                autoComplete="address-level1"
                                value={draft.state}
                                onChange={event => setDraftField('state', event.target.value)}
                              />
                            </label>
                            <label>
                              ZIP code
                              <input
                                id="i20-postalCode"
                                autoComplete="postal-code"
                                value={draft.postalCode}
                                onChange={event => setDraftField('postalCode', event.target.value)}
                              />
                            </label>
                            <label>
                              Country
                              <input
                                id="i20-country"
                                autoComplete="country-name"
                                value={draft.country}
                                onChange={event => setDraftField('country', event.target.value)}
                              />
                            </label>
                            <label className="i20-wide">
                              Referred by
                              <input
                                id="i20-referralName"
                                value={draft.referralName}
                                onChange={event => setDraftField('referralName', event.target.value)}
                              />
                            </label>
                            <label className="i20-wide">
                              Horse details
                              <textarea
                                id="i20-horseNotes"
                                rows={2}
                                value={draft.horseNotes}
                                onChange={event => setDraftField('horseNotes', event.target.value)}
                              />
                            </label>
                          </div>
                        </details>
                        <div className="i20-save-row">
                          <WriteButton
                            paused={paused}
                            id="i20-save"
                            className="cc-blue"
                            disabled={!canSave}
                            onClick={() => setMode('saved')}
                            actionLabel="Save customer"
                          >
                            Save customer <Icon id="check" />
                          </WriteButton>
                          <span>This call stays open.</span>
                        </div>
                      </div>
                    ) : null}
                    {mode === 'held' ? (
                      <div id="i20-held">
                        <strong>Account connection needs review</strong>
                        <p>Keep helping the caller. A manager must resolve the account connection.</p>
                      </div>
                    ) : null}
                    {mode === 'saved' ? (
                      <p id="i20-linked" className="i20-linked" role="status">
                        Caller connected · ready to continue the call.
                      </p>
                    ) : null}
                  </section>
                  <section className="cc-call-guide" aria-label="Call guide">
                    <div className="cc-guide-head">
                      <div>
                        <Icon id="message" />
                        <strong>Call guide</strong>
                      </div>
                    </div>
                    <div className="cc-guide-inner">
                      <p className="cc-guide-reason">Keep helping the caller</p>
                      <p className="cc-guide-primary">
                        Save account details after this call closes. Writes are blocked by
                        LIVE_WRITE_GATE_HUBSPOT_CONTACT_CREATE until Doug approves.
                      </p>
                    </div>
                  </section>
                  <label className="cc-field">
                    Conversation notes
                    <textarea
                      rows={3}
                      placeholder="Record what the caller said and what you helped with"
                    />
                  </label>
                </div>
              </section>
            </div>
            <aside className="cc-stack">
              {useFixture ? (
                <section className="card cc-card">
                  <div className="cc-card-head">
                    <div>
                      <Icon id="clock" />
                      <h2>Requested Call</h2>
                    </div>
                  </div>
                  <div className="cc-body">
                    <div className="cc-callback-summary">
                      <span className="cc-empty-icon">
                        <Icon id="clock" />
                      </span>
                      <strong>No call requested</strong>
                      <p>Record the agreed time and what the customer needs.</p>
                    </div>
                  </div>
                </section>
              ) : (
                <section className="card cc-card" data-testid="intake-pending">
                  <div className="cc-card-head">
                    <div>
                      <Icon id="people" />
                      <h2>Pending intake</h2>
                    </div>
                  </div>
                  <div className="cc-body">
                    <p className="cc-callback-note" data-testid="intake-pending-note">
                      {INTAKE_HONEST_NOTE}
                    </p>
                    <p className="cc-callback-note" data-testid="intake-pending-count">
                      {pending.total !== null
                        ? `${pending.total.toLocaleString('en-US')} unowned contact${pending.total === 1 ? '' : 's'} match`
                        : 'Unowned contacts'}
                      {pending.lookbackDays !== null ? ` · ${pending.lookbackDays}-day window` : ''}
                      {` · showing newest ${pending.items.length}`}
                    </p>
                    {pending.items.length === 0 ? (
                      <div className="cc-callback-summary" data-testid="intake-pending-empty">
                        <span className="cc-empty-icon">
                          <Icon id="people" />
                        </span>
                        <strong>No pending intake</strong>
                        <p>Contacts created without an owner appear here.</p>
                      </div>
                    ) : (
                      <ol data-testid="intake-pending-list">
                        {pending.items.map(row => (
                          <li key={row.id} data-testid="intake-pending-item">
                            <strong>{row.fullName ?? row.email ?? `HubSpot contact ${row.id}`}</strong>
                            <div>
                              {[row.email, row.phone, [row.city, row.state].filter(Boolean).join(', ') || null]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                            <small>
                              {row.createdAt ? `Created ${monthYear(row.createdAt) ?? row.createdAt}` : 'Created date unknown'}
                              {row.lifecycleStage ? ` · ${row.lifecycleStage}` : ''}
                              {row.latestSource ? ` · ${row.latestSource}` : ''}
                            </small>
                            <button
                              type="button"
                              className="cc-link"
                              onClick={() => navigate(`/current-action/${encodeURIComponent(row.id)}`)}
                            >
                              Open record
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </section>
              )}
              <section className="cc-callout">
                <div className="cc-callout-icon">
                  <Icon id="check" />
                </div>
                <div>
                  <strong>Incoming call</strong>
                  <p>
                    {useFixture
                      ? `${JAMIE.fullName} stays with the caller. The intake record is saved after the call.`
                      : 'You stay with the caller. The intake record is saved after the call.'}
                  </p>
                </div>
              </section>
            </aside>
          </div>
        </div>
      </DataBoundary>
      <ActionFooter
        disabled={finishDisabled}
        noteWhenDisabled="Connect the account or save a new customer first"
        noteWhenEnabled="Caller linked · Ready for next customer"
        summaryStrong="Incoming call · Account connection"
        summaryBody="Account connection and new-customer save stay on this screen."
        onFinish={() => navigate(returnTarget.path, { state: returnTarget.state })}
      />
      {scoped.previewAllowed && !stateOverride ? (
        <StatePreview kind={kind} onChange={setKind} />
      ) : null}
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return { kind: 'loading', label: 'Preparing intake…' };
    case 'empty':
      return {
        kind: 'empty',
        title: 'No incoming calls',
        body: 'The intake screen opens when an unrecognized call arrives.',
      };
    case 'error':
      return {
        kind: 'error',
        title: 'Intake could not load',
        detail: 'The intake adapter did not respond.',
        errorCode: 'INTAKE_UNAVAILABLE',
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'i20-intake', reason: 'hubspot_lookup_slow' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: 'Contact create is paused',
        reason: 'Live-write gate OFF for HubSpot Contact Create.',
        gateName: 'LIVE_WRITE_GATE_HUBSPOT_CONTACT_CREATE',
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: 'Saving new customer…',
        actionName: 'hubspot_contact_create',
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StatePreview({
  kind,
  onChange,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
}): ReactNode {
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 16,
        padding: 10,
        border: '1px dashed #c4d4e5',
        borderRadius: 6,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

export default NewCustomerIntakeScreen;
